import { Constant, digitPlaceholderSearch } from '@app/const';
import {
  Design,
  DesignModuleLocalVariable,
  DesignModuleNode,
  PromptObjectInput,
  ValueOrVariableType
} from '@app/types';
import { getPromptInputHauless } from '../design';
import {
  getVariablesFromAllTexts,
  GetVariablesFromAllTextsProps
} from './get-variables-from-all-texts';

export type GetInputsFromAllTextsProps = Omit<GetVariablesFromAllTextsProps, 'removeBraces'> & {
  errorQuietly?: boolean;
  localVariables?: DesignModuleLocalVariable[];
  node: DesignModuleNode;
  serialized: Design;
};

export function getInputsFromAllTexts({
  errorQuietly = false,
  localVariables = [],
  serialized = window.apm.serialized,
  ...props
}: GetInputsFromAllTextsProps): PromptObjectInput[] {
  const contextInputs = (props.node.props.config.inputs ?? []) as PromptObjectInput[];
  const variablesInPrompt = getVariablesFromAllTexts(props);

  const inputs = variablesInPrompt.map((variableString): PromptObjectInput | Error => {
    const model = contextInputs.find((input) => {
      return input.varName === variableString;
    });

    if (model) {
      return model;
    } else if (variableString.match(digitPlaceholderSearch)) {
      return getPromptInputHauless(variableString);
    }

    const [scope, name] = variableString.split('.');
    const variable = (scope === Constant.k.SCOPE_APPLICATION) ? (
      serialized.components.variables.find((variable) => variable.name === name)
    ) : (
      localVariables.find((local) => local.name === name)
    );

    if (!variable) {
      const message = (
        `Variable "${variableString}" used by prompt "${props.node.name}" not found in current module.`
      );
      if (!errorQuietly) {
        const title = 'Missing Variable';
        import('../../../contexts').then(({ defaultServiceContext }) => {
          const { errorMessageService } = defaultServiceContext;
          errorMessageService.addError({ message, title });
        });
      }
      return new Error(message);
    }

    return {
      ...variable.props.config,
      argumentName: variableString,
      type: ValueOrVariableType.variable,
      varName: variableString
    };
  });

  return inputs.filter((input) => {
    return !(input instanceof Error) && Object.keys(input).length;
  }) as PromptObjectInput[];
}
